// ============================================================
// useCalendarInteraction — Canvas クリック/タップ処理
// ============================================================

import { useEffect, useState, useCallback, useRef } from 'react';
import { Vehicle, Reservation, GRID_CONFIG } from './types';
import { GhostPosition, DISPLAY_STATUSES } from './GridCanvas';
import { dateToAbsoluteY, TIME_SLOTS, DAY_HEIGHT_PX } from './useGridGeometry';

const { columnWidth, headerHeight, leftGutterWidth, overlayMinHeight } = GRID_CONFIG;

export interface UseCalendarInteractionProps {
  containerRef: React.RefObject<HTMLDivElement>;
  vehicles: Vehicle[];
  reservations: Reservation[];
  rangeStart: Date;
  dayCount: number;
  onReservationClick: (reservation: Reservation) => void;
  onEmptyCellClick: (vehicleId: string, date: Date) => void;
  /** 予約ダブルクリック → 編集パネル */
  onReservationDblClick?: (reservation: Reservation) => void;
  /** alt+クリック → 自由移動モード */
  onCmdReservationClick?: (reservation: Reservation, shiftKey?: boolean, freeMove?: boolean) => void;
  /** ヘッダークリック */
  onHeaderClick?: (vehicle: Vehicle, shiftKey?: boolean, metaKey?: boolean) => void;
  /** ドラッグで新規予約作成 */
  onDragCreate?: (vehicleId: string, startDate: Date, endDate: Date) => void;
  /** overlay リサイズ（上端/下端ドラッグ） */
  onReservationResize?: (reservation: Reservation, edge: 'top' | 'bottom', newDate: Date) => void;
  /** 移動/入替モードの状態 */
  interactionMode?: 'none' | 'move' | 'move-free' | 'swap' | 'vehicle-swap';
  /** ソース予約（選択済み）の高さ（ゴースト表示用） */
  selectedBlockHeight?: number;
  /** ソース予約の絶対Y座標（ゴーストY位置固定用） */
  selectedAbsoluteY?: number;
  /** hover 中の予約ID（GridCanvas から） */
  hoveredReservationId?: string | null;
  /** ズーム倍率 */
  zoom?: number;
  /** ヘッダー DnD: ドラッグ中のインデックス */
  onHeaderDragStart?: (index: number) => void;
  /** ヘッダー DnD: ドロップターゲット更新 */
  onHeaderDragOver?: (targetIndex: number | null) => void;
  /** ヘッダー DnD: ドロップ */
  onHeaderDrop?: (fromIndex: number, toIndex: number) => void;
  /** ヘッダー DnD: キャンセル */
  onHeaderDragEnd?: () => void;
}

export interface ResizeGhost {
  reservation: Reservation;
  edge: 'top' | 'bottom';
  absTop: number;
  absBottom: number;
  vehicleIndex: number;
}

export interface UseCalendarInteractionReturn {
  ghostPosition: GhostPosition | null;
  dragGhost: GhostPosition | null;
  resizeGhost: ResizeGhost | null;
}

/** 絶対Y座標（ボディピクセル） → Date */
export function absoluteYToDate(rangeStart: Date, absoluteY: number): Date {
  const dayIndex = Math.max(0, Math.floor(absoluteY / DAY_HEIGHT_PX));
  const pixelWithinDay = absoluteY - dayIndex * DAY_HEIGHT_PX;
  const slotIndex = Math.min(
    Math.floor(pixelWithinDay / GRID_CONFIG.slotHeight),
    TIME_SLOTS.length - 1,
  );
  const slot = TIME_SLOTS[slotIndex];
  const hour = slot.type === 'hour' ? slot.hour : (slot.hours[0] ?? 0);
  const dayDate = new Date(rangeStart.getTime() + dayIndex * 86400000);
  dayDate.setHours(hour, 0, 0, 0);
  return dayDate;
}

/** ビューポート座標 → ボディ絶対座標（zoom 対応） */
function viewToBody(viewX: number, viewY: number, scrollX: number, scrollY: number, zoom: number = 1) {
  // ビューポート座標を zoom で割って描画空間に変換し、スクロールも zoom で割る
  const zViewX = viewX / zoom;
  const zViewY = viewY / zoom;
  const zScrollX = scrollX / zoom;
  const zScrollY = scrollY / zoom;
  const bodyX = zViewX - leftGutterWidth + Math.max(0, zScrollX - leftGutterWidth);
  const bodyY = zViewY - headerHeight + Math.max(0, zScrollY - headerHeight);
  return { bodyX, bodyY };
}

export function useCalendarInteraction({
  containerRef,
  vehicles,
  reservations,
  rangeStart,
  onReservationClick,
  onEmptyCellClick,
  onCmdReservationClick,
  onReservationDblClick,
  onHeaderClick,
  onDragCreate,
  onReservationResize,
  interactionMode = 'none',
  selectedBlockHeight = 0,
  selectedAbsoluteY = 0,
  hoveredReservationId,
  zoom = 1,
  onHeaderDragStart,
  onHeaderDragOver,
  onHeaderDrop,
  onHeaderDragEnd,
}: UseCalendarInteractionProps): UseCalendarInteractionReturn {
  const [ghostPosition, setGhostPosition] = useState<GhostPosition | null>(null);
  const [dragGhost, setDragGhost] = useState<GhostPosition | null>(null);
  const [resizeGhost, setResizeGhost] = useState<ResizeGhost | null>(null);
  const ghostRafRef = useRef<number>(0);
  const dragRef = useRef<{ vehicleIndex: number; startAbsY: number; active: boolean }>({ vehicleIndex: -1, startAbsY: 0, active: false });
  /** ドラッグ/リサイズ中の最新 bodyY を追跡（画面外 mouseup 対応） */
  const lastBodyYRef = useRef(0);
  const resizeRef = useRef<{ reservation: Reservation; edge: 'top' | 'bottom'; origAbsTop: number; origAbsBottom: number; vehicleIndex: number; active: boolean } | null>(null);
  const headerDragRef = useRef<{ fromIndex: number; startX: number; active: boolean } | null>(null);

  // クリック + ドラッグ作成処理
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let downX = 0, downY = 0;
    let didDrag = false;
    let downOnHeader = false;
    let downOnCanvas = false;

    const resolveClick = (event: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const viewX = event.clientX - rect.left;
      const viewY = event.clientY - rect.top;
      const scrollX = container.scrollLeft;
      const scrollY = container.scrollTop;

      // zoom 補正
      const zViewX = viewX / zoom;
      const zViewY = viewY / zoom;
      const zScrollX = scrollX / zoom;

      if (zViewX < leftGutterWidth) return;

      // ヘッダークリック — mousedown もヘッダー上だった場合のみ
      if (zViewY < headerHeight && downOnHeader) {
        if (!onHeaderClick) return;
        const bodyX = zViewX - leftGutterWidth + Math.max(0, zScrollX - leftGutterWidth);
        const vehicleIndex = Math.floor(bodyX / columnWidth);
        if (vehicleIndex >= 0 && vehicleIndex < vehicles.length) {
          onHeaderClick(vehicles[vehicleIndex], event.shiftKey, event.metaKey || event.ctrlKey);
        }
        return;
      }
      if (zViewY < headerHeight) return;

      const { bodyX, bodyY } = viewToBody(viewX, viewY, scrollX, scrollY, zoom);
      const vehicleIndex = Math.floor(bodyX / columnWidth);
      if (vehicleIndex < 0 || vehicleIndex >= vehicles.length) return;
      const vehicle = vehicles[vehicleIndex];
      if (!vehicle) return;
      const vehicleId = String(vehicle.id);

      // hover で特定済みの予約を優先使用（Canvas のヒットテストと完全一致）
      if (hoveredReservationId) {
        const hoveredR = reservations.find(r => String(r.id) === hoveredReservationId);
        if (hoveredR) {
          if (event.altKey && onCmdReservationClick) {
            event.preventDefault();
            onCmdReservationClick(hoveredR, false, true);
          } else if (event.shiftKey && onCmdReservationClick) {
            onCmdReservationClick(hoveredR, true, false);
          } else {
            onReservationClick(hoveredR);
          }
          return;
        }
      }

      const clickedDate = absoluteYToDate(rangeStart, bodyY);
      onEmptyCellClick(vehicleId, clickedDate);
    };

    const onMouseDown = (event: MouseEvent) => {
      if (event.button !== 0) return;
      // Canvas 要素以外からのイベントは無視（UI オーバーレイからの伝播防止）
      const target = event.target as HTMLElement;
      if (target.tagName !== 'CANVAS') return;

      downX = event.clientX;
      downY = event.clientY;
      didDrag = false;
      downOnCanvas = true;

      const rect = container.getBoundingClientRect();
      const viewX = event.clientX - rect.left;
      const viewY = event.clientY - rect.top;
      const zVX = viewX / zoom;
      const zVY = viewY / zoom;
      downOnHeader = zVX > leftGutterWidth && zVY < headerHeight;

      // ヘッダー DnD 開始
      if (downOnHeader && onHeaderDragStart) {
        const scrollX = container.scrollLeft / zoom;
        const hBodyX = zVX - leftGutterWidth + Math.max(0, scrollX - leftGutterWidth);
        const hvi = Math.floor(hBodyX / columnWidth);
        if (hvi >= 0 && hvi < vehicles.length) {
          headerDragRef.current = { fromIndex: hvi, startX: event.clientX, active: false };
        }
      }

      if (zVX < leftGutterWidth || zVY < headerHeight) return;
      // 移動/入替モード中はドラッグ作成しない
      if (interactionMode !== 'none') return;

      const { bodyX, bodyY } = viewToBody(viewX, viewY, container.scrollLeft, container.scrollTop, zoom);
      const vi = Math.floor(bodyX / columnWidth);
      if (vi < 0 || vi >= vehicles.length) return;

      // 予約ブロック上: リサイズ判定（上下10px）またはドラッグ作成しない
      // 重なる場合は最小の予約を優先
      const vid = String(vehicles[vi].id);
      const RESIZE_ZONE = 10;
      let onReservation = false;
      let bestResize: { reservation: Reservation; edge: 'top' | 'bottom'; at: number; ab: number; bh: number } | null = null;
      let bestResizeBh = Infinity;
      for (const r of reservations) {
        const rvid = String(r.vehicle_id || '');
        if (rvid !== vid) continue;
        // 表示対象のステータスのみ（tmp_ は新規予約ゴースト）
        if (!DISPLAY_STATUSES.has(r.status) && !String(r.id).startsWith('tmp_')) continue;
        const s = new Date(r.start_date);
        const e = new Date(r.end_date);
        if (isNaN(s.getTime()) || isNaN(e.getTime())) continue;
        const at = dateToAbsoluteY(rangeStart, s);
        const ab = dateToAbsoluteY(rangeStart, e);
        const bh = Math.max(overlayMinHeight, ab - at);
        if (bodyY >= at && bodyY <= at + bh) {
          if (bh < bestResizeBh) {
            onReservation = true;
            bestResizeBh = bh;
            if (bodyY - at < RESIZE_ZONE) {
              bestResize = { reservation: r, edge: 'top', at, ab, bh };
            } else if ((at + bh) - bodyY < RESIZE_ZONE) {
              bestResize = { reservation: r, edge: 'bottom', at, ab, bh };
            } else {
              bestResize = null; // 中央部分 — リサイズなし
            }
          }
        }
      }
      if (bestResize) {
        resizeRef.current = { reservation: bestResize.reservation, edge: bestResize.edge, origAbsTop: bestResize.at, origAbsBottom: bestResize.at + bestResize.bh, vehicleIndex: vi, active: false };
      }
      if (onReservation) return;

      dragRef.current = { vehicleIndex: vi, startAbsY: bodyY, active: false };
    };

    const onMouseMoveForDrag = (event: MouseEvent) => {
      // ヘッダー DnD 処理
      if (headerDragRef.current) {
        const dist = Math.abs(event.clientX - headerDragRef.current.startX);
        if (!headerDragRef.current.active && dist > 8) {
          headerDragRef.current.active = true;
          onHeaderDragStart?.(headerDragRef.current.fromIndex);
        }
        if (headerDragRef.current.active) {
          didDrag = true;
          const rect = container.getBoundingClientRect();
          const zVX = (event.clientX - rect.left) / zoom;
          const scrollX = container.scrollLeft / zoom;
          const hBodyX = zVX - leftGutterWidth + Math.max(0, scrollX - leftGutterWidth);
          // ドロップ位置: 列の境界（列の中央を超えたら右側）
          const targetCol = Math.round(hBodyX / columnWidth);
          const clamped = Math.max(0, Math.min(vehicles.length, targetCol));
          onHeaderDragOver?.(clamped);
          return;
        }
      }

      // リサイズ処理
      if (resizeRef.current) {
        const dist = Math.abs(event.clientY - downY) + Math.abs(event.clientX - downX);
        if (dist < 5) return;
        resizeRef.current.active = true;
        didDrag = true;
        const rect = container.getBoundingClientRect();
        const rvX = event.clientX - rect.left;
        const rvY = event.clientY - rect.top;
        const { bodyY } = viewToBody(rvX, rvY, container.scrollLeft, container.scrollTop, zoom);
        lastBodyYRef.current = bodyY;
        const rz = resizeRef.current;
        const newTop = rz.edge === 'top' ? Math.min(bodyY, rz.origAbsBottom - overlayMinHeight) : rz.origAbsTop;
        const newBottom = rz.edge === 'bottom' ? Math.max(bodyY, rz.origAbsTop + overlayMinHeight) : rz.origAbsBottom;
        setResizeGhost({
          reservation: rz.reservation,
          edge: rz.edge,
          absTop: newTop,
          absBottom: newBottom,
          vehicleIndex: rz.vehicleIndex,
        });
        return;
      }

      if (dragRef.current.vehicleIndex < 0) return;
      const dist = Math.abs(event.clientY - downY) + Math.abs(event.clientX - downX);
      if (dist < 8) return; // 閾値以下は無視

      dragRef.current.active = true;
      didDrag = true;

      const rect = container.getBoundingClientRect();
      const viewX = event.clientX - rect.left;
      const viewY = event.clientY - rect.top;
      const { bodyY } = viewToBody(viewX, viewY, container.scrollLeft, container.scrollTop, zoom);
      lastBodyYRef.current = bodyY;

      const startY = Math.min(dragRef.current.startAbsY, bodyY);
      const endY = Math.max(dragRef.current.startAbsY, bodyY);
      setDragGhost({
        vehicleIndex: dragRef.current.vehicleIndex,
        absoluteY: startY,
        blockHeight: Math.max(overlayMinHeight, endY - startY),
      });
    };

    const onMouseUp = (event: MouseEvent) => {
      // Canvas 上で mousedown していない場合はすべて無視
      if (!downOnCanvas) return;
      downOnCanvas = false;

      // ヘッダー DnD 完了
      if (headerDragRef.current?.active) {
        const rect = container.getBoundingClientRect();
        const zVX = (event.clientX - rect.left) / zoom;
        const scrollX = container.scrollLeft / zoom;
        const hBodyX = zVX - leftGutterWidth + Math.max(0, scrollX - leftGutterWidth);
        const targetCol = Math.round(hBodyX / columnWidth);
        const clamped = Math.max(0, Math.min(vehicles.length, targetCol));
        onHeaderDrop?.(headerDragRef.current.fromIndex, clamped);
        headerDragRef.current = null;
        onHeaderDragEnd?.();
        return;
      }
      if (headerDragRef.current) {
        headerDragRef.current = null;
        onHeaderDragEnd?.();
      }

      // リサイズ完了
      if (resizeRef.current?.active && onReservationResize) {
        const rect = container.getBoundingClientRect();
        const rvX = event.clientX - rect.left;
        const rvY = event.clientY - rect.top;
        let bodyY: number;
        if (rvY < 0 || rvY > rect.height || rvX < 0 || rvX > rect.width) {
          bodyY = lastBodyYRef.current;
        } else {
          ({ bodyY } = viewToBody(rvX, rvY, container.scrollLeft, container.scrollTop, zoom));
        }
        const rz = resizeRef.current;
        const newDate = absoluteYToDate(rangeStart, rz.edge === 'top'
          ? Math.min(bodyY, rz.origAbsBottom - overlayMinHeight)
          : Math.max(bodyY, rz.origAbsTop + overlayMinHeight));
        onReservationResize(rz.reservation, rz.edge, newDate);
      }
      if (resizeRef.current) {
        const wasResize = resizeRef.current.active;
        resizeRef.current = null;
        setResizeGhost(null);
        if (wasResize) return; // リサイズ完了した場合はクリック処理しない
      }

      if (dragRef.current.active && onDragCreate) {
        // 画面外 mouseup 対応: event 座標が Canvas 外ならば lastBodyYRef を使用
        const rect = container.getBoundingClientRect();
        const viewX = event.clientX - rect.left;
        const viewY = event.clientY - rect.top;
        let bodyY: number;
        if (viewY < 0 || viewY > rect.height || viewX < 0 || viewX > rect.width) {
          // 画面外 — 最後に追跡した bodyY を使用
          bodyY = lastBodyYRef.current;
        } else {
          ({ bodyY } = viewToBody(viewX, viewY, container.scrollLeft, container.scrollTop, zoom));
        }
        const startY = Math.min(dragRef.current.startAbsY, bodyY);
        const endY = Math.max(dragRef.current.startAbsY, bodyY);
        const vi = dragRef.current.vehicleIndex;
        if (vi >= 0 && vi < vehicles.length) {
          const vehicleId = String(vehicles[vi].id);
          const startDate = absoluteYToDate(rangeStart, startY);
          const endDate = absoluteYToDate(rangeStart, endY);
          if (endDate > startDate) {
            onDragCreate(vehicleId, startDate, endDate);
          }
        }
      }

      setDragGhost(null);
      const wasDrag = dragRef.current.active;
      dragRef.current = { vehicleIndex: -1, startAbsY: 0, active: false };

      // ドラッグでなければクリックとして処理
      if (!wasDrag && !didDrag) {
        resolveClick(event);
      }
    };

    container.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMoveForDrag);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      container.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMoveForDrag);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [containerRef, vehicles, reservations, rangeStart, onReservationClick, onEmptyCellClick, onCmdReservationClick, onHeaderClick, onDragCreate, onReservationResize, interactionMode, onReservationDblClick, zoom, hoveredReservationId]);

  // ダブルクリック → 予約編集パネル
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !onReservationDblClick) return;
    const onDblClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.tagName !== 'CANVAS') return;
      const rect = container.getBoundingClientRect();
      const viewX = event.clientX - rect.left;
      const viewY = event.clientY - rect.top;
      if (viewX / zoom < leftGutterWidth || viewY / zoom < headerHeight) return;
      const { bodyX, bodyY } = viewToBody(viewX, viewY, container.scrollLeft, container.scrollTop, zoom);
      const vi = Math.floor(bodyX / columnWidth);
      if (vi < 0 || vi >= vehicles.length) return;
      const vid = String(vehicles[vi].id);
      // hover で特定済みの予約を優先使用
      if (hoveredReservationId) {
        const hoveredR = reservations.find(r => String(r.id) === hoveredReservationId);
        if (hoveredR) {
          event.preventDefault();
          onReservationDblClick(hoveredR);
          return;
        }
      }
      let dblBest: Reservation | null = null;
      let dblBestBh = Infinity;
      for (const r of reservations) {
        const rvid = String(r.vehicle_id || '');
        if (rvid !== vid) continue;
        if (!DISPLAY_STATUSES.has(r.status) && !String(r.id).startsWith('tmp_')) continue;
        const s = new Date(r.start_date);
        const e = new Date(r.end_date);
        if (isNaN(s.getTime()) || isNaN(e.getTime())) continue;
        const at = dateToAbsoluteY(rangeStart, s);
        const ab = dateToAbsoluteY(rangeStart, e);
        const bh = Math.max(overlayMinHeight, ab - at);
        if (bodyY >= at && bodyY <= at + bh && bh < dblBestBh) {
          dblBest = r;
          dblBestBh = bh;
        }
      }
      if (dblBest) {
        event.preventDefault();
        onReservationDblClick(dblBest);
      }
    };
    container.addEventListener('dblclick', onDblClick);
    return () => container.removeEventListener('dblclick', onDblClick);
  }, [containerRef, vehicles, reservations, rangeStart, onReservationDblClick, zoom, hoveredReservationId]);

  // マウス追従ゴースト（移動/入替/車両スワップモード）
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const isReservationMode = (interactionMode === 'move' || interactionMode === 'move-free' || interactionMode === 'swap') && selectedBlockHeight > 0;
    const isVehicleSwapMode = interactionMode === 'vehicle-swap';
    if (!isReservationMode && !isVehicleSwapMode) {
      setGhostPosition(null);
      return;
    }

    // 自由移動: 最初のマウス位置からのオフセットを記録
    let initialOffsetY: number | null = null;
    let initialOffsetCol: number | null = null;

    const onMouseMove = (event: MouseEvent) => {
      cancelAnimationFrame(ghostRafRef.current);
      ghostRafRef.current = requestAnimationFrame(() => {
        const rect = container.getBoundingClientRect();
        const viewX = event.clientX - rect.left;
        const viewY = event.clientY - rect.top;
        const zViewX = viewX / zoom;

        if (zViewX < leftGutterWidth) {
          setGhostPosition(null);
          return;
        }

        // vehicle-swap モード: ヘッダー上でも動作（bodyX のみ使用）
        const zScrollX = container.scrollLeft / zoom;
        const bodyX = zViewX - leftGutterWidth + Math.max(0, zScrollX - leftGutterWidth);
        const vehicleIndex = Math.floor(bodyX / columnWidth);
        if (vehicleIndex < 0 || vehicleIndex >= vehicles.length) {
          setGhostPosition(null);
          return;
        }

        if (isVehicleSwapMode) {
          // ヘッダーゴースト: vehicleIndex のみ使用
          setGhostPosition({
            vehicleIndex,
            absoluteY: 0,
            blockHeight: 0,
          });
          return;
        }

        if (viewY / zoom < headerHeight) {
          setGhostPosition(null);
          return;
        }

        const { bodyX: _bx, bodyY } = viewToBody(viewX, viewY, container.scrollLeft, container.scrollTop, zoom);

        if (interactionMode === 'move-free') {
          if (initialOffsetY === null) {
            initialOffsetY = bodyY - selectedAbsoluteY;
            initialOffsetCol = vehicleIndex;
          }
          setGhostPosition({
            vehicleIndex,
            absoluteY: bodyY - initialOffsetY,
            blockHeight: selectedBlockHeight,
          });
        } else {
          setGhostPosition({
            vehicleIndex,
            absoluteY: selectedAbsoluteY,
            blockHeight: selectedBlockHeight,
          });
        }
      });
    };

    const onMouseLeave = () => setGhostPosition(null);

    container.addEventListener('mousemove', onMouseMove, { passive: true });
    container.addEventListener('mouseleave', onMouseLeave);
    return () => {
      container.removeEventListener('mousemove', onMouseMove);
      container.removeEventListener('mouseleave', onMouseLeave);
      cancelAnimationFrame(ghostRafRef.current);
    };
  }, [containerRef, vehicles, interactionMode, selectedBlockHeight, selectedAbsoluteY, zoom]);

  return { ghostPosition, dragGhost, resizeGhost };
}
